import React, { createContext, useState, useEffect, useCallback } from "react";
import * as authService from "../services/authService";
import { AppConfig } from "../config/appConfig";
export const UserContext = createContext();

// En desarrollo forzamos modo autenticado para poder ver/probar la UI
// completa (tabs, PDFs, convenio, etc.) sin depender del flujo real de
// sesión contra produce.gob.pe (que falla por CORS desde localhost).
const IS_DEV = process.env.NODE_ENV === "development";

export const UserProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(() => {
    if (IS_DEV) return true;
    // Legacy fallback por si el build de prod corre en localhost
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      return true;
    }
    return false;
  });

  const [user, setUser] = useState(() => {
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      return { username: "usuario_local", role: "admin" };
    }
    return null;
  });

  // Verificador de sesión activa (ping a un servidor externo)
  const refreshSession = useCallback(async () => {
    try {
      console.log("[UserContext] Consultando estado de sesión (externo)...");
      const response = await fetch(AppConfig.SESION_CHECK_URL, {
        method: "GET",
        headers: {
          "Accept": "application/json"
        },
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        if (data && typeof data.existe === 'boolean') {
          console.log("[UserContext] Estado de sesión recibido:", data.existe);
          setAuthenticated(data.existe);
          return data.existe;
        }
      }
    } catch (error) {
      console.warn("[UserContext] Error al conectar con el servicio de sesión externo:", error.message);
    }
    return false;
  }, []);

  useEffect(() => {
    // En dev nunca consultamos el servidor — authenticated queda fijo en true.
    if (IS_DEV) return;

    refreshSession();
    const intervalId = setInterval(refreshSession, 60000);
    return () => clearInterval(intervalId);
  }, [refreshSession]);

  const clearSession = useCallback(() => {
    authService.logout();
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    const produceCookies = [
      ".AspNet.ApplicationCookieExtranet",
      ".AspNet.SharedCookie.Extranet"
    ];

    const domains = [
      window.location.hostname,
      `.${window.location.hostname}`,
      ".produce.gob.pe",
      "produce.gob.pe"
    ];

    const publicUrl = process.env.PUBLIC_URL === "." ? "" : (process.env.PUBLIC_URL || "");
    const paths = ["/", window.location.pathname, (publicUrl || "/")];

    produceCookies.forEach(name => {
      domains.forEach(domain => {
        paths.forEach(path => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
        });
      });
    });

    // Limpiar todas las cookies restantes accesibles (solo dominio actual)
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      paths.forEach(path => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
      });
    });

    // Redireccionar al portal externo para forzar el cierre de sesión allá, evadiendo el CORS.
    const redirectPortal = () => {
      setUser(null);
      setAuthenticated(false);

      if (window.location.hostname.includes("produce.gob.pe") && window.location.hostname !== "localhost") {
        // Al redirigir a este panel, el usuario podrá cerrar sesión oficialmente desde su perfil
        window.location.href = AppConfig.PORTAL_URL;
      } else {
        window.location.reload();
      }
    };

    redirectPortal();
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn("[UserContext] Unauthorized event received. Clearing session...");
      clearSession();
    };

    window.addEventListener("unauthorized", handleUnauthorized);

    // Ya no dependemos del token en la URL, verificamos sesión directamente.
    // La sesión real la mantiene la cookie HttpOnly en .produce.gob.pe

    return () => window.removeEventListener("unauthorized", handleUnauthorized);
  }, [clearSession]);

  const handleLogin = async (username, password) => {
    try {
      const data = await authService.login(username, password);
      if (data && data.token) {
        setUser(data.user || { username, role: "user" });
        setAuthenticated(true);
      }
    } catch (error) {
      // Mock login for development
      if (username && password) {
        const fakeUser = { username, role: "admin" };
        setUser(fakeUser);
        setAuthenticated(true);
        localStorage.setItem("user", JSON.stringify(fakeUser));
        localStorage.setItem("token", "fake-jwt-token");
        return;
      }
      throw error;
    }
  };

  const handleLogout = () => {
    clearSession();
  };

  return (
    <UserContext.Provider value={{ user, authenticated, handleLogin, handleLogout, clearSession, refreshSession }}>
      {children}
    </UserContext.Provider>
  );
};
